import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RefreshCw } from 'lucide-react';

/**
 * CaptchaWidget — renders the appropriate CAPTCHA challenge.
 * Props:
 *   type        — "math" | "recaptcha_v2" | "hcaptcha" | "turnstile"
 *   siteKey     — public site key (required for external providers)
 *   onVerified  — cb(true/false) for math captcha (client-side)
 *   onToken     — cb(token) for external providers
 */
const CaptchaWidget = ({ type, siteKey, onVerified, onToken }) => {
  // ── Math CAPTCHA ──────────────────────────────────────────────────────────
  const [nums, setNums] = useState(() => [
    Math.floor(Math.random() * 10) + 1,
    Math.floor(Math.random() * 10) + 1,
  ]);
  const [answer, setAnswer] = useState('');

  const refresh = useCallback(() => {
    setNums([Math.floor(Math.random() * 10) + 1, Math.floor(Math.random() * 10) + 1]);
    setAnswer('');
    if (onVerified) onVerified(false);
  }, [onVerified]);

  useEffect(() => {
    if (type === 'math' && onVerified) {
      const correct = nums[0] + nums[1];
      onVerified(answer.trim() !== '' && parseInt(answer, 10) === correct);
    }
  }, [answer, nums, type, onVerified]);

  // ── External CAPTCHA script loader ────────────────────────────────────────
  const containerRef = useRef(null);
  const widgetId = useRef(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  const SCRIPT_URLS = {
    recaptcha_v2: 'https://www.google.com/recaptcha/api.js?render=explicit',
    hcaptcha: 'https://js.hcaptcha.com/1/api.js?render=explicit',
    turnstile: 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit',
  };

  useEffect(() => {
    if (type === 'math' || !siteKey) return;

    const scriptSrc = SCRIPT_URLS[type];
    if (!scriptSrc) return;

    // Check if script already loaded
    const existing = document.querySelector(`script[src="${scriptSrc}"]`);
    if (existing) {
      setScriptLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = scriptSrc;
    script.async = true;
    script.onload = () => setScriptLoaded(true);
    document.head.appendChild(script);

    return () => {
      // Don't remove the script on unmount — it may be reused
    };
  }, [type, siteKey]);

  useEffect(() => {
    if (!scriptLoaded || !containerRef.current || !siteKey || type === 'math') return;
    if (widgetId.current !== null) return;

    const renderWidget = () => {
      try {
        if (type === 'recaptcha_v2' && window.grecaptcha?.render) {
          widgetId.current = window.grecaptcha.render(containerRef.current, {
            sitekey: siteKey,
            callback: (token) => onToken && onToken(token),
            'expired-callback': () => onToken && onToken(''),
          });
        } else if (type === 'hcaptcha' && window.hcaptcha?.render) {
          widgetId.current = window.hcaptcha.render(containerRef.current, {
            sitekey: siteKey,
            callback: (token) => onToken && onToken(token),
            'expired-callback': () => onToken && onToken(''),
          });
        } else if (type === 'turnstile' && window.turnstile?.render) {
          widgetId.current = window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            callback: (token) => onToken && onToken(token),
            'expired-callback': () => onToken && onToken(''),
          });
        } else {
          // Retry after 200ms if SDK not ready yet
          setTimeout(renderWidget, 200);
        }
      } catch { /* ignore */ }
    };

    renderWidget();
  }, [scriptLoaded, siteKey, type, onToken]);

  // ── Render ────────────────────────────────────────────────────────────────
  if (type === 'math') {
    return (
      <div className="space-y-2" data-testid="math-captcha">
        <Label className="text-sm">CAPTCHA Verification</Label>
        <div className="flex items-center gap-3">
          <div className="flex-1 font-mono text-sm bg-muted/50 border border-border rounded-lg px-4 py-2.5 text-center select-none tracking-widest">
            {nums[0]} + {nums[1]} = ?
          </div>
          <Input
            type="number"
            placeholder="Answer"
            value={answer}
            onChange={e => setAnswer(e.target.value)}
            className="w-24 bg-transparent border-border text-center"
            data-testid="math-captcha-input"
          />
          <button type="button" onClick={refresh} className="p-2 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors" title="New question">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
        {answer && parseInt(answer, 10) !== nums[0] + nums[1] && (
          <p className="text-xs text-destructive" data-testid="math-captcha-error">Incorrect answer, please try again</p>
        )}
      </div>
    );
  }

  // External CAPTCHA container
  return (
    <div data-testid={`${type}-captcha`}>
      {siteKey ? (
        <div ref={containerRef} />
      ) : (
        <p className="text-xs text-amber-500 border border-amber-500/20 rounded-lg px-3 py-2 bg-amber-500/5">
          CAPTCHA site key not configured. Please contact support.
        </p>
      )}
    </div>
  );
};

export default CaptchaWidget;
